// Top of the roster's service panel: date and time, title, publish state,
// notes, the Publish / Republish / Save draft actions and the service menu.
import { useRef, useState } from 'react';
import { CalendarClock, ChevronLeft, Mail, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import type { ServicePlan } from '@app/types';
import { Button, LockedChip, Menu, MenuDivider, StatusPill, cx } from '@app/components/ui';
import { formatTime12 } from '../../utils/rosterHelpers';

interface RosterHeaderProps {
  selectedPlan: ServicePlan | null;
  selectedDate: Date;
  onPublishClick: () => void;
  onEditDetails: () => void;
  onDeleteService: () => void;
  onReschedule?: () => void;
  /** Throw away unsaved changes (shown while there are any). */
  onDiscard?: () => void;
  /** Omitted when the church has no lyrics recipient configured. */
  onResendLyrics?: () => void;
  isResendingLyrics?: boolean;
  onSaveDraft: () => void;
  isPublishing?: boolean;
  hasUnsavedChanges?: boolean;
  onBack?: () => void;
  /** Past services are a record: no publishing, editing or deleting. */
  readOnly?: boolean;
}

const menuItemClass =
  'flex items-center gap-2.5 w-full text-left px-2.5 py-[9px] rounded-[11px] border-0 bg-transparent cursor-pointer text-[14px] hover:bg-fill focus-visible:bg-fill focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed';

export const RosterHeader: React.FC<RosterHeaderProps> = ({
  selectedPlan,
  selectedDate,
  onPublishClick,
  onEditDetails,
  onDeleteService,
  onReschedule,
  onDiscard,
  onResendLyrics,
  isResendingLyrics,
  onSaveDraft,
  isPublishing,
  hasUnsavedChanges,
  onBack,
  readOnly,
}) => {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsRef = useRef<HTMLButtonElement>(null);
  const close = () => setOptionsOpen(false);

  const longDate = selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div>
      {onBack && (
        <button type="button" onClick={onBack} className="min-[760px]:hidden flex items-center gap-1 text-[13.5px] text-link mb-3">
          <ChevronLeft size={16} aria-hidden="true" /> Services
        </button>
      )}
      <div className="flex justify-between items-start gap-2.5">
        <div className="text-[12.5px] text-t4 min-w-0">
          {longDate}
          {selectedPlan ? ` · ${formatTime12(selectedPlan.date)}` : ''}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
          {readOnly && <LockedChip />}
          {selectedPlan && !readOnly && (selectedPlan.isPublished ? <StatusPill status="Published" /> : <StatusPill status="Draft" />)}
          {hasUnsavedChanges && !readOnly && <StatusPill tone="amb">Unsaved</StatusPill>}
          {selectedPlan && (
            <>
            {/* Icon-only, so it needs an explicit accessible name. */}
            <button
              ref={optionsRef}
              type="button"
              onClick={() => setOptionsOpen(!optionsOpen)}
              aria-label="Service options"
              aria-haspopup="menu"
              aria-expanded={optionsOpen}
              data-testid="roster-options-button"
              className="w-8 h-8 rounded-full border border-line2 bg-fill text-t3 hover:text-t1 hover:bg-fill2 flex items-center justify-center"
            >
              <MoreHorizontal size={16} aria-hidden="true" />
            </button>
            <Menu open={optionsOpen} onClose={close} anchorRef={optionsRef} label="Service options" width={240} align="end">
              {!readOnly && (
                <button type="button" role="menuitem" tabIndex={-1} className={cx(menuItemClass, 'text-t1')} onClick={() => { close(); onEditDetails(); }}>
                  <Pencil size={15} aria-hidden="true" /> Edit details
                </button>
              )}
              {onReschedule && (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  data-testid="roster-reschedule-button"
                  className={cx(menuItemClass, 'text-t1')}
                  onClick={() => { close(); onReschedule(); }}
                >
                  <CalendarClock size={15} aria-hidden="true" /> Reschedule…
                </button>
              )}
              {onResendLyrics && selectedPlan.isSetlistPublished && (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  disabled={isResendingLyrics}
                  data-testid="roster-resend-lyrics-button"
                  className={cx(menuItemClass, 'text-t1')}
                  onClick={() => { close(); onResendLyrics(); }}
                >
                  <Mail size={15} aria-hidden="true" /> {isResendingLyrics ? 'Sending…' : 'Resend lyrics email'}
                </button>
              )}
              {!readOnly && (
                <>
                  <MenuDivider />
                  <button type="button" role="menuitem" tabIndex={-1} className={cx(menuItemClass, 'text-acc')} onClick={() => { close(); onDeleteService(); }}>
                    <Trash2 size={15} aria-hidden="true" /> Delete service
                  </button>
                </>
              )}
            </Menu>
            </>
          )}
        </div>
      </div>
      <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-1 leading-tight">{selectedPlan?.title ?? 'No service selected'}</h2>

      {selectedPlan?.notes && (
        <p className="mt-3 text-[13px] text-t2 leading-normal whitespace-pre-wrap bg-fill rounded-[12px] px-3 py-2.5">{selectedPlan.notes}</p>
      )}

      {selectedPlan && !readOnly && (!selectedPlan.isPublished || hasUnsavedChanges) && (
        <div className="flex flex-wrap items-center gap-2 mt-3.5">
          {!readOnly &&
            (selectedPlan.isPublished && hasUnsavedChanges ? (
              <Button size="md" onClick={onPublishClick} disabled={isPublishing} className="!bg-amb hover:!opacity-90">
                {isPublishing ? 'Publishing…' : 'Republish'}
              </Button>
            ) : !selectedPlan.isPublished ? (
              <Button size="md" onClick={onPublishClick} disabled={isPublishing}>
                {isPublishing ? 'Publishing…' : 'Publish roster'}
              </Button>
            ) : null)}

          {!readOnly && !selectedPlan.isPublished && hasUnsavedChanges && (
            <Button variant="ghost" size="md" onClick={onSaveDraft}>
              Save Draft
            </Button>
          )}

          {!readOnly && hasUnsavedChanges && onDiscard && (
            <Button variant="plain" size="md" className="!text-t3 hover:!text-acc" onClick={onDiscard}>
              Discard changes
            </Button>
          )}

        </div>
      )}
    </div>
  );
};
